import { useTranslation } from 'react-i18next'
import { AlertOctagon, CheckCircle2, Clock, Heart, MessageSquare } from 'lucide-react'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { KpiCard } from '@/components/kpi/KpiCard'
import { AnimatedNumber } from '@/components/kpi/AnimatedNumber'
import { Delta } from '@/components/kpi/Delta'
import { formatNumber } from '@/lib/format'
import { IncidentBoard } from '@/components/incidents/IncidentBoard'
import { CsatTrend } from '@/components/charts/CsatTrend'
import { NpsGauge } from '@/components/charts/NpsGauge'
import { IncidentCategoryDonut } from '@/components/charts/IncidentCategoryDonut'
import { formatRelativeAge } from '@/lib/format'
import { now } from '@/lib/clock'

export function SupportSatisfactionSlide() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  if (!snapshot) return null

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {}
      <div className="grid grid-cols-5 gap-4">
        <KpiCard
          label={t('kpi.openIncidents')}
          icon={<AlertOctagon className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.incidents.open}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={`${snapshot.incidents.daysWithoutP1} ${locale === 'cs' ? 'dní bez P1' : 'days without P1'}`}
          tone="warn"
          emphasis
        />
        <KpiCard
          label={t('kpi.closedToday')}
          icon={<CheckCircle2 className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.incidents.closedToday}
              format={(n) => formatNumber(n, locale)}
            />
          }
          caption={`${snapshot.support.ticketsToday} ${t('kpi.ticketsToday').toLowerCase()}`}
          footer={
            <Delta value={snapshot.incidents.closedToday - snapshot.incidents.open} suffix="" />
          }
          tone="good"
        />
        <KpiCard
          label={t('kpi.mttr')}
          icon={<Clock className="size-4" />}
          value={
            <AnimatedNumber
              value={snapshot.incidents.mttrMin}
              format={(n) => `${formatNumber(n, locale)} min`}
            />
          }
          caption={`${t('kpi.avgResponse')}: ${snapshot.incidents.avgResponseMin} min`}
          tone="brand"
        />
        <KpiCard
          label={t('kpi.csat')}
          icon={<Heart className="size-4" />}
          value={<AnimatedNumber value={snapshot.support.csat} format={(n) => n.toFixed(2)} />}
          caption="/ 5"
          tone="good"
        />
        <KpiCard
          label={t('kpi.nps')}
          icon={<MessageSquare className="size-4" />}
          value={
            <AnimatedNumber value={snapshot.support.nps} format={(n) => formatNumber(n, locale)} />
          }
          caption={locale === 'cs' ? 'Net Promoter Score' : 'Net Promoter Score'}
          tone="brand"
        />
      </div>

      {}
      <div className="grid grid-cols-12 gap-4">
        <section className="col-span-4 rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {t('kpi.nps')} · {t('kpi.csat')}
          </h3>
          <NpsGauge
            value={snapshot.support.nps}
            label="NPS"
            secondaryValue={snapshot.support.csat}
            secondaryLabel="CSAT"
          />
        </section>
        <section className="col-span-4 rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {t('support.csatTrend')}
          </h3>
          <CsatTrend data={snapshot.support.csatHistory} height={180} />
        </section>
        <section className="col-span-4 rounded-3xl border border-border bg-surface-1/70 p-5">
          <h3 className="mb-3 text-xs uppercase tracking-[0.18em] text-ink-subtle">
            {t('incidents.categoryBreakdown')}
          </h3>
          <IncidentCategoryDonut data={snapshot.incidents.categoryBreakdown} height={180} />
        </section>
      </div>

      {}
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4">
        <div className="col-span-8">
          <IncidentBoard limit={6} />
        </div>
        <section className="col-span-4 flex flex-col gap-3 rounded-3xl border border-brand-primary/30 bg-gradient-to-br from-surface-1/70 to-brand-primary/5 p-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-primary">
            <Heart className="size-3.5" />
            {t('support.storyOfDay')}
          </div>
          {snapshot.incidents.storyOfTheDay ? (
            <>
              <p className="font-display text-xl font-semibold leading-snug text-ink">
                "{snapshot.incidents.storyOfTheDay.title}"
              </p>
              <div className="mt-auto space-y-1 text-sm text-ink-muted">
                <div>{snapshot.incidents.storyOfTheDay.locationName}</div>
                <div className="text-xs text-ink-subtle">
                  {formatRelativeAge(snapshot.incidents.storyOfTheDay.closedAt, locale, now())}
                </div>
              </div>
            </>
          ) : (
            <p className="text-ink-muted">{t('incidents.empty')}</p>
          )}
        </section>
      </div>
    </div>
  )
}
